import { assertCondition } from '../../../../utils/types';
import { PathNode } from './pathNode';

/**
 * Represents a weighted link between 2 PathNodes. When building the graph, each FlowStep becomes a PathLink
 * Allows updating link source and destination
 */
export class PathLink {
  /**
   * Create a new link from sourceNode to destNode with given weight
   * It modifies sourceNode's outgoing links and destNode incoming links
   *
   * The factory method removes the need to suppress ts-lint rule for unused "new" expression if we create the link
   * by just calling:
   *   new PathLink(sourceNode, destNode, weight)
   */
  public static create(
    sourceNode: PathNode,
    destNode: PathNode,
    weight: number,
  ) {
    return new PathLink(sourceNode, destNode, weight);
  }

  private sourceNode: PathNode;
  private destNode: PathNode;
  private weight: number;

  private constructor(
    sourceNode: PathNode,
    destNode: PathNode,
    weight: number,
  ) {
    this.weight = weight;
    this.sourceNode = sourceNode;
    this.destNode = destNode;
    this.linkNodes();
  }

  /**
   * Updates the destination of the link by destroying any existing links between the nodes, updating destination
   * and relinking them again. It is OK if the link between the sourceNode and newDestNode exist: it the link is merged
   *
   * @param newDestNode
   */
  public updateDestNode(newDestNode: PathNode) {
    this.unlinkNodes();
    this.destNode = newDestNode;
    return this.linkNodes();
  }

  /**
   * Updates the source of the link by destroying any existing links between the nodes, updating the source
   * and relinking them again. It is OK if the link between the newSourceNode and destNode exist: it the link is merged
   *
   * @param newSourceNode
   */
  public updateSourceNode(newSourceNode: PathNode) {
    this.unlinkNodes();
    this.sourceNode = newSourceNode;
    return this.linkNodes();
  }

  /**
   * Merges the otherLink into this link. Both link must have the same source and destination, otherwise it is probably
   * a bug. Current merge strategy is simply summing the weights.
   *
   * @param otherLink
   */
  public merge(otherLink: PathLink) {
    // strong invariant: it doesn't make sense to merge links that doesn't have the same source and destination
    assertCondition(this.sourceNode === otherLink.sourceNode);
    assertCondition(this.destNode === otherLink.destNode);
    this.weight += otherLink.weight;
  }

  public getSourceNode() {
    return this.sourceNode;
  }

  public getDestNode() {
    return this.destNode;
  }

  public getWeight() {
    return this.weight;
  }

  public debug() {
    return {
      __type: 'PathLink',
      sourceNode: this.sourceNode.key,
      destNode: this.destNode.key,
      weight: this.weight,
    };
  }

  private unlinkNodes() {
    return this.sourceNode.unlinkWith(this.destNode);
  }

  private linkNodes() {
    return this.sourceNode.linkWith(this);
  }
}
